import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { toast } from '@/components/ui/sonner'
import { useLandingPageQuery } from '@/hooks/landingPageApiHooks'
import type { SourceStorefrontRef } from './PrefaceComposer'

/**
 * Resolves the `?sourceStorefront=<hash>` deep link into a composer seed.
 *
 * The hash is the selection and comes straight from the URL, so it is returned
 * immediately and nothing waits on the network. The name is only a label, filled
 * in when the lookup lands — which keeps a slow, cached-failed, or refetching
 * request from having any say over whether the composer exists.
 */
export function useSourceStorefrontSeed(): SourceStorefrontRef | null {
    const router = useRouter()
    const hash =
        router.isReady && typeof router.query.sourceStorefront === 'string'
            ? router.query.sourceStorefront
            : undefined
    const { data: landingPage, isError } = useLandingPageQuery(hash)

    // Only the label failed — the hash is still pinned and the build resolves
    // it server-side — so this warns without claiming the flow is dead. isError
    // stays true across a failed refetch and clears only on success, so it
    // fires once per genuine failure rather than on every retry.
    useEffect(() => {
        if (isError) {
            toast.error(
                "Couldn't load that storefront's details. Check it still exists before building.",
            )
        }
    }, [isError])

    if (!hash) return null
    return { hash, name: landingPage?.name }
}
